import { useEffect, useRef, useState } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, FileDown, Upload, CheckCircle2, XCircle, Copy, ListChecks, UploadCloud, CalendarClock,
} from "lucide-react";

function FilaBadge({ fila }) {
  if (fila.duplicada)
    return <span className="inline-flex items-center gap-1 text-[11px] text-blue-700"><Copy className="h-3.5 w-3.5" /> Ya existe</span>;
  if (fila.ok && fila.cambio)
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-violet-700"
        data-testid={`importar-row-cambio-${fila.row_id}`}
      >
        <CalendarClock className="h-3.5 w-3.5" /> Cambio
      </span>
    );
  if (fila.ok)
    return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Válida</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title={fila.problemas.join(", ")}>
      <XCircle className="h-3.5 w-3.5" /> {fila.problemas[0]}
    </span>
  );
}

// PROMPT 154: textos cortos de la carga masiva del equipo.
const fechaCorta = (f) => {
  const p = String(f || "").split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : (f || "—");
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

function resultadoTxt(r) {
  const partes = [];
  if (r.creadas) partes.push(plural(r.creadas, "reserva cargada", "reservas cargadas"));
  if (r.cambiadas) partes.push(plural(r.cambiadas, "cambiada", "cambiadas"));
  if (r.omitidas) partes.push(plural(r.omitidas, "omitida", "omitidas"));
  return partes.length ? partes.join(" · ") : "No se cargó ni se cambió ninguna reserva";
}

function MapeoPanel({ mapeo }) {
  if (!mapeo) return null;
  const usados = (mapeo.campos || []).filter((c) => c.columna);
  const faltantes = (mapeo.campos || []).filter((c) => !c.columna && c.obligatoria);
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 space-y-1.5"
         data-testid="importar-mapeo-panel">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        Columnas detectadas del archivo
      </div>
      <div className="flex flex-wrap gap-1.5">
        {usados.map((c) => (
          <span key={c.campo}
                data-testid={`importar-mapeo-${c.campo}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] text-slate-700">
            <span className="font-mono text-slate-500">{c.columna}</span>
            <span className="text-slate-400">→</span>
            <span className="font-medium">{c.etiqueta}</span>
          </span>
        ))}
      </div>
      {(mapeo.sin_usar || []).length > 0 && (
        <div className="text-[11px] text-amber-800" data-testid="importar-mapeo-sin-usar">
          <span className="font-medium">Columnas sin usar:</span>{" "}
          {mapeo.sin_usar.map((c) => <span key={c} className="font-mono">{c} </span>)}
          <span className="text-amber-700">— esos datos no se van a cargar.</span>
        </div>
      )}
      {faltantes.length > 0 && (
        <div className="text-[11px] text-rose-700" data-testid="importar-mapeo-faltantes">
          <span className="font-medium">Sin detectar (obligatorias):</span>{" "}
          {faltantes.map((c) => c.etiqueta).join(", ")}
        </div>
      )}
    </div>
  );
}

/**
 * Diálogo de carga masiva de reservas (archivo CSV/Excel), reusado en:
 * - /reservas (admin+operador): basePath="/reservas"
 * - Portal de Agencias: basePath="/portal"
 */
export default function ImportarReservasDialog({ open, onOpenChange, basePath = "/reservas", onImported }) {
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [seleccion, setSeleccion] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);
  // PROMPT 154: solo la carga del equipo (/reservas) cambia fecha y hora y trae el origen.
  const esEquipo = basePath === "/reservas";
  const [agencias, setAgencias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [agenciaDef, setAgenciaDef] = useState("");
  const [cuentaDef, setCuentaDef] = useState("");
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!open || !esEquipo) return undefined;
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get("/agencias", { params: { activo: true } });
        const lista = Array.isArray(data) ? data : (data?.items || []);
        if (vivo) setAgencias(lista);
      } catch (e) { /* sin selector de agencia */ }
      try {
        const { data } = await api.get("/marcas");
        const lista = Array.isArray(data) ? data : (data?.items || []);
        if (vivo) setMarcas(lista.filter((m) => m.activo !== false));
      } catch (e) { /* sin selector de cuenta */ }
    })();
    return () => { vivo = false; };
  }, [open, esEquipo]);

  const reset = () => {
    setBatch(null);
    setSeleccion(new Set());
    if (fileRef.current) fileRef.current.value = "";
    setAgenciaDef("");
    setCuentaDef("");
    setResultado(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const descargarPlantilla = async () => {
    try {
      await downloadFile(`${basePath}/import/plantilla`, "plantilla_carga_reservas.csv");
      toast.success("Plantilla CSV descargada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const descargarPlantillaXlsx = async () => {
    try {
      await downloadFile(`${basePath}/import/plantilla-xlsx`, "plantilla_carga_reservas.xlsx");
      toast.success("Plantilla Excel descargada (Fecha y Hora como texto).");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const previewArchivo = async (file) => {
    if (!file) return;
    setLoading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      const { data } = await api.post(`${basePath}/import/preview`, fd);
      setBatch(data);
      const sel = new Set((data.filas || []).filter((f) => f.ok && !f.duplicada).map((f) => f.row_id));
      setSeleccion(sel);
      toast.success(`Vista previa lista: ${data.total} fila(s).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (rowId) => {
    setSeleccion((s) => {
      const c = new Set(s);
      if (c.has(rowId)) c.delete(rowId);
      else c.add(rowId);
      return c;
    });
  };

  const confirmar = async () => {
    if (!batch || seleccion.size === 0) {
      toast.error("Seleccioná al menos una reserva para cargar.");
      return;
    }
    setConfirming(true);
    try {
      const { data } = await api.post(`${basePath}/import/batches/${batch.id}/confirmar`, {
        incluir_ids: Array.from(seleccion),
        ...(esEquipo ? { agencia_id: agenciaDef || null, marca_slug: cuentaDef || null } : {}),
      });
      const dupTxt = data.omitidas_duplicado ? ` (${data.omitidas_duplicado} por duplicado)` : "";
      if (!esEquipo) toast.success(`${data.creadas} reserva(s) cargada(s)${data.omitidas ? ` · ${data.omitidas} omitida(s)${dupTxt}` : ""}.`);
      if (esEquipo) {
        toast.success(`${resultadoTxt(data)}.`);
        if (data.omitidas > 0) {
          // PROMPT 154: las omitidas se muestran con su motivo antes de cerrar.
          setResultado(data);
          onImported?.();
          return;
        }
      }
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const descartar = async () => {
    if (batch) {
      try {
        await api.post(`${basePath}/import/batches/${batch.id}/descartar`);
      } catch (e) { /* noop */ }
    }
    reset();
  };

  // PROMPT 154: origen de la fila (el del archivo, o el elegido arriba en gris).
  const origenFila = (f) => {
    if (f.cambio) return <span className="text-slate-300">—</span>;
    const o = f.origen || {};
    const agElegida = agencias.find((a) => a.id === agenciaDef);
    const cuElegida = marcas.find((m) => m.slug === cuentaDef);
    const ag = (o.agencia_nombre || o.agencia_texto)
      ? <span className="text-slate-700">{o.agencia_nombre || o.agencia_texto}</span>
      : (agElegida ? <span className="text-slate-400">{agElegida.nombre}</span> : null);
    const cu = (o.marca_nombre || o.cuenta_texto)
      ? <span className="text-slate-700">{o.marca_nombre || o.cuenta_texto}</span>
      : (cuElegida ? <span className="text-slate-400">{cuElegida.nombre}</span> : null);
    if (!ag && !cu) return <span className="text-slate-300">—</span>;
    return <>{ag}{ag && cu ? " · " : ""}{cu}</>;
  };

  const filasSel = (batch?.filas || []).filter((f) => seleccion.has(f.row_id));
  const nCambiar = filasSel.filter((f) => f.cambio).length;
  const nCargar = filasSel.length - nCambiar;
  const textoConfirmar = !esEquipo
    ? `Cargar ${seleccion.size || ""} reserva(s)`
    : [nCargar > 0 ? `Cargar ${nCargar}` : "", nCambiar > 0 ? `${nCargar > 0 ? "y cambiar" : "Cambiar"} ${nCambiar}` : ""]
        .filter(Boolean).join(" ") || "Cargar";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="importar-reservas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-slate-700" /> Carga masiva de reservas
          </DialogTitle>
          <DialogDescription>
            Subí un archivo CSV o Excel con varias reservas (por ejemplo, las que llegaron por WhatsApp o sin
            plantilla). Vas a poder revisar cada fila antes de confirmar la carga.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
        <div className="space-y-3" data-testid="importar-resultado">
          <p className="text-sm text-slate-800" data-testid="importar-resultado-resumen">{resultadoTxt(resultado)}.</p>
          <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-1 max-h-[260px] overflow-y-auto">
            <div className="text-[11px] font-medium uppercase tracking-wider text-amber-800">Filas omitidas</div>
            {(resultado.detalle || []).map((d, i) => (
              <div
                key={d.row_id || i}
                className="text-[12px] text-slate-700"
                data-testid={`importar-resultado-omitida-${d.row_id}`}
              >
                <span className="font-mono">{d.codigo || "sin código"}</span>
                {d.pasajero ? ` · ${d.pasajero}` : ""} — {d.error}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-1">
            <Button
              onClick={() => { reset(); onOpenChange(false); }}
              data-testid="importar-resultado-cerrar"
            >
              Cerrar
            </Button>
          </div>
        </div>
      ) : !batch ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => previewArchivo(e.target.files?.[0])}
                disabled={loading}
                data-testid="importar-file-input"
              />
              {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400 shrink-0" />}
            </div>
            <p className="text-[11px] text-slate-400">
              <Upload className="h-3 w-3 inline" /> Las columnas se detectan automáticamente (Tour, Fecha, Hora,
              Adultos, Nombre, etc.). Si no tenés el archivo armado, descargá la plantilla.
            </p>
            <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800"
                 data-testid="importar-formato-hora-aviso">
              <span className="font-medium">Formato de Fecha y Hora:</span> la <span className="font-medium">Fecha</span> va
              como <span className="font-mono">AAAA-MM-DD</span> (ej. 2026-07-29) y la <span className="font-medium">Hora</span> en
              su propia columna, en <span className="font-mono">HH:MM</span> de 24 horas (ej. <span className="font-mono">09:00</span>,{" "}
              <span className="font-mono">23:00</span>). Si la columna Hora queda vacía, la fila NO se carga: te la marcamos
              como "Falta la hora". La hora tiene que ser un horario real del tour para ese día.
            </div>
            {esEquipo && (
            <p className="text-[11px] text-slate-500" data-testid="importar-ayuda-cambios">
              Columnas opcionales al final: <span className="font-medium">Agencia</span> y{" "}
              <span className="font-medium">Cuenta</span> (de dónde vino la reserva). Si el código ya existe con otra
              fecha u hora, la fila se carga como un cambio de fecha y hora.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={descargarPlantilla}
                data-testid="importar-descargar-plantilla"
                className="gap-2"
              >
                <FileDown className="h-4 w-4" /> Descargar plantilla (CSV)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={descargarPlantillaXlsx}
                data-testid="importar-descargar-plantilla-xlsx"
                className="gap-2"
              >
                <FileDown className="h-4 w-4" /> Descargar plantilla (Excel)
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
              <div className="flex items-center gap-3 text-sm">
                <ListChecks className="h-4 w-4 text-slate-500" />
                <span className="text-slate-700">
                  {esEquipo ? (
            <span data-testid="importar-resumen">
              {plural(batch.total, "fila", "filas")} ·{" "}
              <span className="text-emerald-700">{plural(batch.validas || 0, "válida", "válidas")}</span> ·{" "}
              <span className="text-violet-700">{plural(batch.cambios || 0, "cambio", "cambios")}</span> ·{" "}
              <span className="text-amber-700">{batch.con_problemas} con problemas</span> ·{" "}
              <span className="text-blue-700">
                {batch.duplicadas} {batch.duplicadas === 1 ? "ya existe" : "ya existen"} (sin cambios)
              </span>
            </span>
          ) : (
            <>
              {batch.total} filas · <span className="text-emerald-700">{batch.validas} válidas</span> ·{" "}
                  <span className="text-amber-700">{batch.con_problemas} con problemas</span> ·{" "}
                  <span className="text-blue-700">{batch.duplicadas} ya existen</span>
            </>
          )}
                </span>
              </div>
              <span className="text-xs text-slate-500">{seleccion.size} seleccionadas</span>
            </div>
            <MapeoPanel mapeo={batch.mapeo_detalle} />
        {esEquipo && (
          <div
            className="grid gap-2 sm:grid-cols-2 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2"
            data-testid="importar-origen-defecto"
          >
            <label className="block text-[11px] font-medium text-slate-600">
              Agencia de origen
              <select
                value={agenciaDef}
                onChange={(e) => setAgenciaDef(e.target.value)}
                className="mt-1 block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm font-normal text-slate-800"
                data-testid="importar-agencia-defecto"
              >
                <option value="">Ninguna</option>
                {agencias.map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </label>
            <label className="block text-[11px] font-medium text-slate-600">
              Cuenta / marca
              <select
                value={cuentaDef}
                onChange={(e) => setCuentaDef(e.target.value)}
                className="mt-1 block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm font-normal text-slate-800"
                data-testid="importar-cuenta-defecto"
              >
                <option value="">Ninguna</option>
                {marcas.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.nombre}</option>
                ))}
              </select>
            </label>
            <p className="sm:col-span-2 text-[11px] text-slate-500">
              Para las filas que no la traen. Si el archivo trae las columnas «Agencia» y «Cuenta», mandan las del archivo.
            </p>
          </div>
        )}
            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow className="hover:bg-white">
                      {["", "Pasajero", "Código", "Tour (archivo)", "Tour del catálogo", "Fecha", "Hora leída", "Salida destino", "Pax", ...(esEquipo ? ["Origen"] : []), "Estado"].map((h, i) => (
                        <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.filas.map((f) => {
                      const n = f.normalizada;
                      const pax = (n.adultos || 0) + (n.ninos || 0) + (n.infantes || 0);
                      return (
                        <TableRow
                          key={f.row_id}
                          data-testid={`importar-row-${f.row_id}`}
                          className={f.duplicada ? "bg-blue-50/30" : !f.ok ? "bg-amber-50/30" : f.cambio ? "bg-violet-50/40" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={seleccion.has(f.row_id)}
                              onCheckedChange={() => toggleRow(f.row_id)}
                              data-testid={`importar-row-check-${f.row_id}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm text-slate-800">{n.nombre} {n.apellido}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap" data-testid={`importar-row-codigo-${f.row_id}`}>
                            {f.codigo ? (
                              <span className="font-mono text-slate-800">{f.codigo}</span>
                            ) : (
                              <span className="text-amber-700">Se autogenera</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-[150px] truncate"
                                     title={f.tour_texto_archivo || ""}
                                     data-testid={`importar-row-tour-archivo-${f.row_id}`}>
                            {f.tour_texto_archivo || "—"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600 max-w-[160px]"
                                     data-testid={`importar-row-tour-catalogo-${f.row_id}`}>
                            {f.tour?.nombre || (
                              <span className="text-amber-700">
                                {f.tour_match?.ambiguo ? "Ambiguo · elegir a mano" : "Sin identificar"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {f.cambio && f.cambio.fecha[0] !== f.cambio.fecha[1] ? (
                  <span className="font-mono text-violet-700" data-testid={`importar-row-fecha-${f.row_id}`}>
                    {fechaCorta(f.cambio.fecha[0])} → {fechaCorta(f.cambio.fecha[1])}
                  </span>
                ) : (
                  <span className="font-mono text-slate-700">{n.fecha || "—"}</span>
                )}
                          </TableCell>
                          <TableCell className="text-sm" data-testid={`importar-row-hora-${f.row_id}`}>
                            <span className={`font-mono font-medium ${n.hora ? "text-slate-800" : "text-amber-700"}`}>
                              {f.cambio && f.cambio.hora[0] !== f.cambio.hora[1] ? `${f.cambio.hora[0]} → ${f.cambio.hora[1]}` : (n.hora || "sin hora")}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-slate-600 whitespace-nowrap"
                                     data-testid={`importar-row-destino-${f.row_id}`}>
                            {f.salida_destino ? (
                              <span className={f.salida_destino.existe ? "text-slate-700" : "text-indigo-700"}>
                                {f.salida_destino.existe ? "Salida " : "Se crea "}
                                {f.salida_destino.etiqueta}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                            {f.cambio?.nota && (
                      <div
                        className="text-[10px] text-slate-500 whitespace-normal max-w-[190px] mt-0.5"
                        data-testid={`importar-row-nota-${f.row_id}`}
                      >
                        Nota: {f.cambio.nota}
                      </div>
                    )}
                    {(f.avisos || []).length > 0 && (
                              <div className="text-[10px] text-amber-700 whitespace-normal max-w-[190px] mt-0.5"
                                   data-testid={`importar-row-aviso-${f.row_id}`}>
                                {f.avisos.map((a, i) => <div key={i}>{a}</div>)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600 whitespace-nowrap">{pax} pax</TableCell>
                          {esEquipo && (
                      <TableCell className="text-xs whitespace-nowrap" data-testid={`importar-row-origen-${f.row_id}`}>
                        {origenFila(f)}
                      </TableCell>
                    )}
                    <TableCell><FilaBadge fila={f} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={descartar} data-testid="importar-descartar-button">
                Descartar
              </Button>
              <Button
                onClick={confirmar}
                disabled={confirming || seleccion.size === 0}
                data-testid="importar-confirmar-button"
                className="gap-2"
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {textoConfirmar}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
